
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import useAuth from '../hooks/useAuth.ts';
import logoAuth from '../Images/logo.svg';
import styles from './login.module.css';

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

const SignInForm = () => {
  const [email, setEmailInput] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(true);
  const [authMode, setAuthMode] = useState(null);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { 
    authenticated,
    setAuthenticated, 
    setName, 
    setEmail, 
    setRoles, 
    setOrganizationIds 
  } = useAuth();
  
  const from = location.state?.from?.pathname || '/fleet';

  useEffect(() => {
    const initAuth = async () => {
      if (authenticated) {
        navigate(from, { replace: true });
        return;
      }

      try {
        const response = await axios.get(`${backendUrl}/api/auth/status`);
        const mode = response.data.auth_mode;
        setAuthMode(mode);
        
        const params = new URLSearchParams(window.location.search);
        if (params.get('error')) {
          setError(params.get('error_description') || 'Authentication failed');
        }
      } catch (e) {
        console.error('Failed to get auth mode:', e);
        setAuthMode('password');
      }
      
      setChecking(false);
    };

    initAuth();
  }, []);

  useEffect(() => {
    if (authenticated) {
      navigate(from, { replace: true });
    }
  }, [authenticated, navigate, from]);

  const handleAuthSuccess = (data) => {
    const { jwt, username, email, roles, organizationIds } = data;
    
    localStorage.setItem('token', jwt);
    localStorage.setItem('isLoggedIn', 'true');
    localStorage.setItem('name', username);
    localStorage.setItem('email', email);
    localStorage.setItem('roles', JSON.stringify(roles));
    localStorage.setItem('organizationIds', JSON.stringify(organizationIds));
    
    axios.defaults.headers.common['Authorization'] = `Bearer ${jwt}`;
    
    setAuthenticated(true);
    setName(username);
    setEmail(email);
    setRoles(roles);
    setOrganizationIds(organizationIds);
    
    navigate(from, { replace: true });
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setError('');
    
    try {
      const response = await axios.post(`${backendUrl}/login`, { 
        email, 
        password 
      });
      
      handleAuthSuccess(response.data);
    } catch (err) {
      if (err.response?.status === 403 && err.response?.data?.auth_mode === 'microsoft') {
        setError('Password login is disabled in production. Please use Microsoft authentication.');
        setAuthMode('microsoft');
      } else {
        setError(err.response?.data?.message || 'Login failed. Please try again.');
      }
    }
  };

  const handleMicrosoftLogin = () => {
    // Full-page navigation into Easy Auth. After Microsoft sign-in, EasyAuth
    // bounces (same-origin to the backend) to /api/auth/finish-login, which
    // mints a JWT and 302-redirects to ${FRONTEND_URL}/auth/callback#jwt=...
    // No cross-site cookie is ever read by the SPA.
    const authUrl = `${backendUrl}/.auth/login/aad`;
    const postLoginUrl = `${backendUrl}/api/auth/finish-login`;
    window.location.href = `${authUrl}?post_login_redirect_uri=${encodeURIComponent(postLoginUrl)}`;
  };

  if (checking) {
    return (
      <section className={styles.section}>
        <div className={styles.signIn}>
          <div className={styles.logo}>
            <img src={logoAuth} alt="logo" className={styles.user_img} />
          </div>
          <p>Checking authentication...</p>
        </div>
      </section>
    );
  }

  return (
    <section className={styles.section}>
      <div className={styles.signIn}>
        <header>
          <div className={styles.logo}>
            <img src={logoAuth} alt="logo" className={styles.user_img} />
          </div>
          <p className={styles.heading}>Welcome to PadTrack</p>
          <p className={styles.subHeading}>Sign in to continue</p>
        </header>

        {error && (
          <div style={{
            padding: '10px',
            marginTop: '20px',
            backgroundColor: '#f8d7da',
            color: '#721c24',
            borderRadius: '4px',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        {authMode === 'password' && (
          <form onSubmit={handlePasswordLogin} style={{ marginTop: '30px' }}>
            <div style={{ marginBottom: '15px' }}>
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmailInput(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '16px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <div style={{ marginBottom: '15px' }}>
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '16px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <button
              type="submit"
              style={{
                width: '100%',
                padding: '12px 20px',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '16px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              Sign In
            </button>
            
            <p style={{ 
              textAlign: 'center', 
              marginTop: '15px', 
              color: '#666',
              fontSize: '12px' 
            }}>
              🔧 Local Development Mode
            </p>
          </form>
        )}

        {authMode === 'microsoft' && (
          <>
            <button 
              onClick={handleMicrosoftLogin}
              style={{
                width: '100%',
                padding: '12px 20px',
                backgroundColor: '#0078d4',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '16px',
                fontWeight: '500',
                cursor: 'pointer',
                marginTop: '30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px'
              }}
            >
              <svg width="21" height="21" viewBox="0 0 21 21" fill="none">
                <rect x="0" y="0" width="10" height="10" fill="#f25022"/>
                <rect x="11" y="0" width="10" height="10" fill="#7fba00"/>
                <rect x="0" y="11" width="10" height="10" fill="#00a4ef"/>
                <rect x="11" y="11" width="10" height="10" fill="#ffb900"/>
              </svg>
              Sign in with Microsoft
            </button>

            <p style={{ 
              textAlign: 'center', 
              marginTop: '20px', 
              color: '#666',
              fontSize: '14px' 
            }}>
              Use your ProFrac Microsoft account
            </p>
          </>
        )}
      </div>
    </section>
  );
};

export { SignInForm };
export default SignInForm;